import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {PaletteColorsEnum} from '../../../../../libs/shared/core/src/models/api/paletteModel';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {SimpleDropdownWithHeader} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownWithHeader';
import {PopoverTransferProps} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {IncidentImpactsEnum, IncidentModel, IncidentTypesEnum} from '../../../../models/api/incidentModel';

/*
 * Constants.
 */

const impactColorPreset = {
  [IncidentImpactsEnum.NONE]: PaletteColorsEnum.GREY,
  [IncidentImpactsEnum.MAINTENANCE]: PaletteColorsEnum.GREY,
  [IncidentImpactsEnum.MINOR]: PaletteColorsEnum.YELLOW,
  [IncidentImpactsEnum.MAJOR]: PaletteColorsEnum.ORANGE,
  [IncidentImpactsEnum.CRITICAL]: PaletteColorsEnum.RED,
};

/*
 * Props.
 */

interface IncidentsDropdownProps {
  popoverProps: PopoverTransferProps;
  incidents: ReadonlyArray<IncidentModel>;
  onOpenUrlClick: (arg: string) => void;
}

/*
 * Styles.
 */

const StyledSimpleDropdownWithHeader = styled(SimpleDropdownWithHeader)`
  margin-top: 4px;
`;

const StyledIncidentContentDiv = styled.div`
  grid-area: content;
  overflow: hidden;
  max-width: 100%;
`;

const StyledImpactWrapperDiv = styled.div`
  display: flex;
`;

const StyledImpactDiv = styled.div<{$impact: IncidentImpactsEnum}>`
  margin: 2px 0px 5px;
  padding: 1px 5px 2px;
  border-radius: 8px;

  font-size: ${fontSizes.tiny};
  font-weight: ${fontWeights.bold};

  color: ${(p) => p.theme.palette[impactColorPreset[p.$impact]].shade50};
  background-color: ${(p) => p.theme.palette[impactColorPreset[p.$impact]].shade30};
`;

const StyledIncidentNameDiv = styled.div`
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};
  margin-bottom: 3px;
  word-break: break-word;
`;

const StyledIncidentBodyDiv = styled.div`
  font-size: ${fontSizes.verySmall};
  color: ${(p) => p.theme.greys.shade70};
  margin: 1px auto 5px;
  word-break: break-word;
`;

/*
 * Component.
 */

export const IncidentsDropdown: FC<IncidentsDropdownProps> = ({popoverProps, incidents, onOpenUrlClick}) => {
  const hasPartnerIncident = incidents.some((incident) => incident.type === IncidentTypesEnum.PARTNER);

  const renderIncident = (incident: IncidentModel) => (
    <DropdownItem
      key={incident.id}
      onSelect={() => onOpenUrlClick(incident.shortlink)} // TODO Redirect to Helpdesk page for partner incidents when it's ready.
      isNotInteractive={incident.type !== IncidentTypesEnum.FRONT} // TODO Remove when helpdesk article is ready.
    >
      <StyledIncidentContentDiv>
        <StyledImpactWrapperDiv>
          <StyledImpactDiv $impact={incident.impact}>{renderIncidentType(incident)}</StyledImpactDiv>
        </StyledImpactWrapperDiv>
        <StyledIncidentNameDiv>{incident.name}</StyledIncidentNameDiv>
        {incident.body && <StyledIncidentBodyDiv>{incident.body}</StyledIncidentBodyDiv>}
      </StyledIncidentContentDiv>
    </DropdownItem>
  );

  return (
    <StyledSimpleDropdownWithHeader
      {...popoverProps}
      width={300}
      title={<Trans id="incidents-title">Front Status</Trans>}
      placement="top-end"
      footer={maybeRenderPartnerIncidentFooter(hasPartnerIncident)}
    >
      {incidents.map(renderIncident)}
    </StyledSimpleDropdownWithHeader>
  );
};

/*
 * Helpers.
 */

const renderIncidentType = (incident: IncidentModel) => {
  switch (incident.type) {
    case IncidentTypesEnum.FRONT:
      return incident.impact === 'maintenance' ? (
        <Trans id="topbar-maintenance-front">Front service maintenance</Trans>
      ) : (
        <Trans id="topbar-incident-front">Front service incident</Trans>
      );

    case IncidentTypesEnum.PARTNER:
      return <Trans id="topbar-incident-partner">External service incident</Trans>;

    default:
      return '';
  }
};

const maybeRenderPartnerIncidentFooter = (hasPartnerIncident: boolean) => {
  if (!hasPartnerIncident) {
    return null;
  }

  return (
    <Trans id="partner-incidents-dropdown-footer">
      A Front partner is experiencing a temporary disruption.
    </Trans>
  );
};
